import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { TeamInfo, MatchupConfig, MatchupSummary, TeamConfigRequest, League, InsightsResponse, Stage } from '../types';
import { apiService } from '../services/api';
import {
  clearRecommendationCaches,
  clearRecommendationRequestInFlight,
  getRecommendationCache,
  markRecommendationRequestInFlight,
  buildRecommendationCacheKey,
  setRecommendationCache
} from '../utils/recommendationsCache';
import { useAuth } from './AuthContext';
import { apiFetch } from '../utils/httpClient';

interface TeamContextType {
  // Team configuration
  matchupConfig: MatchupConfig | null;
  setMatchupConfig: (config: MatchupConfig) => void;
  matchupSummary: MatchupSummary | null;
  
  
  // League data
  leagues: League[];
  selectedLeague: League | null;
  setSelectedLeague: (league: League | null) => void;
  
  // Stage data
  stages: Stage[];
  isStagesLoading: boolean;
  stagesError: string | null;
  
  // Team data
  userTeam: TeamInfo | null;
  opponentTeam: TeamInfo | null;
  allTeams: TeamInfo[];
  easternTeams: TeamInfo[];
  westernTeams: TeamInfo[];
  
  // Team stats from MongoDB
  opponentTeamStats: any | null;
  userTeamStats: any | null;
  isStatsLoading: boolean;
  statsError: string | null;
  
  // Team insights data
  teamInsights: any | null;
  playerInsights: any | null;
  lineupsInsights: any | null;
  opponentTeamInsights: any | null;
  opponentPlayerInsights: any | null;
  opponentLineupsInsights: any | null;
  isInsightsLoading: boolean;
  insightsError: string | null;
  
  // Splits data
  teamSplits: string[] | null;
  playerSplits: string[] | null;
  lineupSplits: string[] | null;
  isSplitsLoading: boolean;
  splitsError: string | null;
  
  // Default splits for initialization
  defaultSplits: string[] | null;
  
  // Loading states
  isLoading: boolean;
  isConfiguring: boolean;
  error: string | null;
  
  // Actions
  loadTeamStats: (splitKeys?: string[]) => Promise<void>;
  refreshTeamStats: (splitKeys?: string[]) => Promise<void>;
  setTeamConfiguration: (config: TeamConfigRequest) => Promise<boolean>;
  refreshConfiguration: () => Promise<void>;
  clearConfiguration: () => Promise<void>;
  loadAllTeams: () => Promise<void>;
  loadLeagues: () => Promise<void>;
  loadStages: (league: string) => Promise<void>;
  loadTeamsByLeague: (league: string) => Promise<void>;
  clearCache: () => void;
  forceReloadConfiguration: () => Promise<void>;
  
  // Selective stats loading methods
  loadTeamStatsOnly: (splitKeys?: string[]) => Promise<void>;
  loadPlayerStatsOnly: (splitKeys?: string[]) => Promise<void>;
  loadLineupStatsOnly: (splitKeys?: string[]) => Promise<void>;
  
  // Selective insights fetching methods
  fetchTeamInsights: (config?: MatchupConfig, splitKeys?: string[], forceRefresh?: boolean) => Promise<void>;
  fetchPlayerInsights: (config?: MatchupConfig, splitKeys?: string[], forceRefresh?: boolean) => Promise<void>;
  fetchLineupInsights: (config?: MatchupConfig, splitKeys?: string[], forceRefresh?: boolean) => Promise<void>;
  
  // Insights refresh methods
  refreshTeamInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  refreshPlayerInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  refreshLineupInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  
  // Opponent insights refresh methods
  refreshOpponentTeamInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  refreshOpponentPlayerInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  refreshOpponentLineupInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  
  // Individual insight refresh methods (no global loading state)
  refreshIndividualTeamInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  refreshIndividualPlayerInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  refreshIndividualLineupInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  refreshIndividualOpponentTeamInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  refreshIndividualOpponentPlayerInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  refreshIndividualOpponentLineupInsights: (insightIds: string[], splitKeys?: string[], insightIndex?: number) => Promise<InsightsResponse | null>;
  
  // Splits methods
  loadTeamSplits: (league: string) => Promise<void>;
  loadPlayersSplits: (league: string) => Promise<void>;
  loadLineupsSplits: (league: string) => Promise<void>;
  
  // Current stats type for selective loading
  currentStatsType: 'team' | 'players' | 'lineups';
  setCurrentStatsType: (type: 'team' | 'players' | 'lineups') => void;
  
  // Clear current stats data
  clearCurrentStats: () => void;
  
  // Clear all insights data
  clearAllInsights: () => void;
  
  // Helper methods
  getUserTeam: () => TeamInfo | null;
  getOpponentTeam: () => TeamInfo | null;
  getMatchupTitle: () => string;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

interface TeamProviderProps {
  children: ReactNode;
}

export const TeamProvider: React.FC<TeamProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // Team configuration state
  const [matchupConfig, setMatchupConfigState] = useState<MatchupConfig | null>(null);
  const [matchupSummary, setMatchupSummary] = useState<MatchupSummary | null>(null);
  
  // Track user authentication changes
  useEffect(() => {
  }, [user, matchupConfig]);
  
  // League state
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  
  // Stage state
  const [stages, setStages] = useState<Stage[]>([]);
  const [isStagesLoading, setIsStagesLoading] = useState<boolean>(false);
  const [stagesError, setStagesError] = useState<string | null>(null);
  
  // Team data state
  const [allTeams, setAllTeams] = useState<TeamInfo[]>([]);
  const [easternTeams, setEasternTeams] = useState<TeamInfo[]>([]);
  const [westernTeams, setWesternTeams] = useState<TeamInfo[]>([]);
  
  // Team stats state
  const [opponentTeamStats, setOpponentTeamStats] = useState<any | null>(null);
  const [userTeamStats, setUserTeamStats] = useState<any | null>(null);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  
  // Team insights state
  const [teamInsights, setTeamInsights] = useState<any | null>(null);
  const [playerInsights, setPlayerInsights] = useState<any | null>(null);
  const [lineupsInsights, setLineupsInsights] = useState<any | null>(null);
  const [opponentTeamInsights, setOpponentTeamInsights] = useState<any | null>(null);
  const [opponentPlayerInsights, setOpponentPlayerInsights] = useState<any | null>(null);
  const [opponentLineupsInsights, setOpponentLineupsInsights] = useState<any | null>(null);
  const [isInsightsLoading, setIsInsightsLoading] = useState<boolean>(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  
  // Splits state
  const [teamSplits, setTeamSplits] = useState<string[] | null>(null);
  const [playerSplits, setPlayerSplits] = useState<string[] | null>(null);
  const [lineupSplits, setLineupSplits] = useState<string[] | null>(null);
  const [isSplitsLoading, setIsSplitsLoading] = useState<boolean>(false);
  const [splitsError, setSplitsError] = useState<string | null>(null);
  
  // Default splits state
  const [defaultSplits, setDefaultSplits] = useState<string[] | null>(null);
  
  // Current stats type for selective loading
  const [currentStatsType, setCurrentStatsType] = useState<'team' | 'players' | 'lineups'>('team');
  
  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isConfiguring, setIsConfiguring] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const lastPrefetchKeyRef = useRef<string | null>(null);
  
  // Refs to track loading states and prevent duplicate calls
  const isLoadingLeaguesRef = useRef<boolean>(false);
  const isLoadingTeamsRef = useRef<string | null>(null);
  const isLoadingStagesRef = useRef<string | null>(null);
  const isLoadingConfigRef = useRef<boolean>(false);
  const isLoadingPlayerStatsRef = useRef<boolean>(false);
  
  // Refs to track current state values to avoid stale closures
  const leaguesRef = useRef<League[]>(leagues);
  const allTeamsRef = useRef<TeamInfo[]>(allTeams);
  
  // Update refs when state changes
  useEffect(() => {
    leaguesRef.current = leagues;
  }, [leagues]);
  
  useEffect(() => {
    allTeamsRef.current = allTeams;
  }, [allTeams]);

  // Set matchup config and trigger stats loading
  const setMatchupConfig = useCallback((config: MatchupConfig) => {
    setMatchupConfigState(config);
    // Create matchup summary
    const summary: MatchupSummary = {
      user_team: config.user_team,
      opponent_team: config.opponent_team,
      matchup_title: `${config.user_team.teamName} vs ${config.opponent_team.teamName}`,
      season: config.season,
      matchup_type: config.matchup_type,
      is_conference_matchup: false, // No longer available in simplified structure
      is_division_matchup: false // No longer available in simplified structure
    };
    setMatchupSummary(summary);
    // Clear cache when configuration changes - will be handled by clearCache function
    // Remove automatic insights fetching - insights should only be fetched when explicitly requested
    // fetchAllInsights(config);
    // Note: Stats loading is handled by the Reports component when it detects config changes
  }, []);


  // Load team stats from MongoDB
  const loadTeamStats = useCallback(async (splitKeys?: string[]) => {
    // Block all stats calls until configuration is fully loaded
    if (isLoadingConfigRef.current) {
      console.log('🔄 Configuration still loading, blocking team stats fetch');
      return;
    }

    // Validate league configuration before making API calls
    if (!selectedLeague || !selectedLeague.abbreviation || selectedLeague.abbreviation.trim() === '') {
      console.log('❌ No league configured - blocking team stats API call');
      console.log('🔍 selectedLeague:', selectedLeague);
      // Trigger configuration modal - this will be handled by the App component
      return;
    }
    
    if (!matchupConfig) {
      console.log('No matchup config available for loading stats');
      return;
    }

    // Prevent multiple simultaneous calls
    if (isStatsLoading) {
      console.log('Stats already loading, skipping duplicate call');
      return;
    }

    setIsStatsLoading(true);
    setStatsError(null);

    try {
      // Load both user team and opponent team stats
      const [userStats, opponentStats] = await Promise.all([
        apiService.getTeamStats(
          selectedLeague.abbreviation,
          matchupConfig.user_team.teamId,
          matchupConfig.season,
          matchupConfig.matchup_type,
          splitKeys
        ),
        apiService.getTeamStats(
          selectedLeague.abbreviation,
          matchupConfig.opponent_team.teamId,
          matchupConfig.season,
          matchupConfig.matchup_type,
          splitKeys
        )
      ]);
      
      if (userStats) {
        setUserTeamStats(userStats);
      } else {
        console.log('❌ Failed to load user team stats');
      }
      
      if (opponentStats) {
        setOpponentTeamStats(opponentStats);
      } else {
        console.log('❌ Failed to load opponent team stats');
      }

    } catch (error) {
      console.error('❌ Error loading team stats:', error);
      setStatsError(error instanceof Error ? error.message : 'Failed to load team stats');
    } finally {
      setIsStatsLoading(false);
    }
  }, [matchupConfig, selectedLeague]);

  // Refresh team stats
  const refreshTeamStats = useCallback(async (splitKeys?: string[]) => {
    await loadTeamStats(splitKeys);
  }, [loadTeamStats]);

  // Clear current stats data (only opponent - pre-game page shows opponent data)
  const clearCurrentStats = useCallback(() => {
    console.log('Clearing opponent stats data');
    setOpponentTeamStats(null);
    setStatsError(null);
  }, []);

  // Clear all insights data (only opponent - pre-game page shows opponent data)
  const clearAllInsights = useCallback(() => {
    console.log('Clearing opponent insights data');
    setOpponentTeamInsights(null);
    setOpponentPlayerInsights(null);
    setOpponentLineupsInsights(null);
    setInsightsError(null);
  }, []);

  // Selective stats loading methods
  const loadTeamStatsOnly = useCallback(async (splitKeys?: string[]) => {
    // Block all stats calls until configuration is fully loaded
    if (isLoadingConfigRef.current) {
      console.log('🔄 Configuration still loading, blocking team stats fetch');
      return;
    }

    // Validate league configuration before making API calls
    if (!selectedLeague || !selectedLeague.abbreviation || selectedLeague.abbreviation.trim() === '') {
      console.log('❌ No league configured - blocking team stats API call');
      console.log('🔍 selectedLeague:', selectedLeague);
      // Trigger configuration modal - this will be handled by the App component
      return;
    }
    
    if (!matchupConfig) {
      console.log('No matchup config available for loading team stats');
      return;
    }

    if (isStatsLoading) {
      console.log('Stats already loading, skipping duplicate call');
      return;
    }

    setIsStatsLoading(true);
    setStatsError(null);

    try {
      // Only fetch opponent stats - pre-game page only shows opponent data
      const opponentStats = await apiService.getTeamStats(
        selectedLeague.abbreviation,
        matchupConfig.opponent_team.teamId,
        matchupConfig.season,
        matchupConfig.matchup_type,
        splitKeys
      );
      
      if (opponentStats) {
        setOpponentTeamStats(opponentStats);
      }

    } catch (error) {
      console.error('❌ Error loading team stats:', error);
      setStatsError(error instanceof Error ? error.message : 'Failed to load team stats');
    } finally {
      setIsStatsLoading(false);
    }
  }, [matchupConfig, selectedLeague]);

  const loadPlayerStatsOnly = useCallback(async (splitKeys?: string[]) => {
    // Block all stats calls until configuration is fully loaded
    if (isLoadingConfigRef.current) {
      console.log('🔄 Configuration still loading, blocking player stats fetch');
      return;
    }

    // Validate league configuration before making API calls
    if (!selectedLeague || !selectedLeague.abbreviation || selectedLeague.abbreviation.trim() === '') {
      console.log('❌ No league configured - blocking player stats API call');
      console.log('🔍 selectedLeague:', selectedLeague);
      // Trigger configuration modal - this will be handled by the App component
      return;
    }
    
    if (!matchupConfig) {
      console.log('No matchup config available for loading player stats');
      return;
    }

    // Use dedicated ref for player stats to prevent overlapping requests
    if (isLoadingPlayerStatsRef.current || isStatsLoading) {
      console.log('Player stats already loading, skipping duplicate call');
      return;
    }

    isLoadingPlayerStatsRef.current = true;
    setIsStatsLoading(true);
    setStatsError(null);

    try {
      console.log(`Loading player stats only for ${matchupConfig.season} ${matchupConfig.matchup_type} season`);
      
      // Always include 'overall' split along with any user-selected splits
      const splitsToFetch = ['overall'];
      if (splitKeys && splitKeys.length > 0) {
        splitsToFetch.push(...splitKeys);
      }
      
      // Only fetch opponent stats - pre-game page only shows opponent data
      const opponentStatsPromises = splitsToFetch.map(split => 
        apiService.getPlayerStats(
          selectedLeague.abbreviation,
          matchupConfig.opponent_team.teamId,
          matchupConfig.season,
          matchupConfig.matchup_type,
          split
        ).then(data => {
          // Add split property to each player in the response
          if (Array.isArray(data)) {
            return data.map(player => ({ ...player, split }));
          }
          return data;
        })
      );
      
      const opponentStatsResults = await Promise.all(opponentStatsPromises);
      
      // Flatten and merge the results
      const mergedOpponentStats = opponentStatsResults.flat();
      
      if (mergedOpponentStats.length > 0) {
        setOpponentTeamStats(mergedOpponentStats);
        console.log('✅ Opponent player stats loaded');
      }

    } catch (error) {
      console.error('❌ Error loading player stats:', error);
      setStatsError(error instanceof Error ? error.message : 'Failed to load player stats');
    } finally {
      setIsStatsLoading(false);
      isLoadingPlayerStatsRef.current = false;
    }
  }, [matchupConfig, selectedLeague]);

  const loadLineupStatsOnly = useCallback(async (splitKeys?: string[]) => {
    // Block all stats calls until configuration is fully loaded
    if (isLoadingConfigRef.current) {
      console.log('🔄 Configuration still loading, blocking lineup stats fetch');
      return;
    }

    // Validate league configuration before making API calls
    if (!selectedLeague || !selectedLeague.abbreviation || selectedLeague.abbreviation.trim() === '') {
      console.log('❌ No league configured - blocking lineup stats API call');
      console.log('🔍 selectedLeague:', selectedLeague);
      // Trigger configuration modal - this will be handled by the App component
      return;
    }
    
    if (!matchupConfig) {
      console.log('No matchup config available for loading lineup stats');
      return;
    }

    if (isStatsLoading) {
      console.log('Stats already loading, skipping duplicate call');
      return;
    }

    setIsStatsLoading(true);
    setStatsError(null);

    try {
      console.log(`Loading lineup stats only for ${matchupConfig.season} ${matchupConfig.matchup_type} season`);
      
      // Only fetch opponent stats - pre-game page only shows opponent data
      const opponentStats = await apiService.getLineupsStats(
        selectedLeague.abbreviation,
        matchupConfig.opponent_team.teamId,
        matchupConfig.season,
        matchupConfig.matchup_type,
        5,
        splitKeys
      );
      
      if (opponentStats) {
        setOpponentTeamStats(opponentStats);
        console.log('✅ Opponent lineup stats loaded');
      }

    } catch (error) {
      console.error('❌ Error loading lineup stats:', error);
      setStatsError(error instanceof Error ? error.message : 'Failed to load lineup stats');
    } finally {
      setIsStatsLoading(false);
    }
  }, [matchupConfig, selectedLeague]);

  // Selective insights fetching methods
  const fetchTeamInsights = useCallback(async (config?: MatchupConfig, splitKeys?: string[], forceRefresh?: boolean) => {
    // Block all insights calls until configuration is fully loaded
    if (isLoadingConfigRef.current) {
      console.log('🔄 [INSIGHTS DEBUG] Configuration still loading, blocking team insights fetch');
      return;
    }
    
    const configToUse = config || matchupConfig;
    if (!configToUse) {
      console.log('❌ [INSIGHTS DEBUG] No matchup config available for loading team insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return;
    }

    if (!selectedLeague) {
      console.log('❌ [INSIGHTS DEBUG] No league configured for loading team insights - will retry after configuration is loaded');
      setInsightsError('League is not configured. Please select a league in settings.');
      
      // Retry after a short delay to allow configuration to complete
      setTimeout(() => {
        if (!isLoadingConfigRef.current && selectedLeague) {
          console.log('🔄 [INSIGHTS DEBUG] Retrying team insights fetch - configuration and league are now ready');
          fetchTeamInsights(config, splitKeys, forceRefresh);
        } else {
          console.log('🔄 [INSIGHTS DEBUG] Retry conditions not met - still loading or no league');
        }
      }, 500);
      return;
    }

    // Note: Removed currentStatsType check to prevent race condition
    // The calling code already knows which type to fetch

    // REMOVE the check for already loaded insights
    // This allows refetching when splits change

    if (isInsightsLoading) {
      console.log('Insights already loading, skipping duplicate call');
      return;
    }

    setIsInsightsLoading(true);
    setInsightsError(null);

    try {
      // Only fetch opponent insights - pre-game page only shows opponent data
      const opponentInsights = await apiService.getTeamInsights(
        configToUse.opponent_team.teamId,
        configToUse.season,
        configToUse.matchup_type,
        splitKeys,
        selectedLeague.abbreviation,
        forceRefresh
      );
      
      setOpponentTeamInsights(opponentInsights);

    } catch (error) {
      console.error('❌ Error loading team insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to load team insights');
    } finally {
      setIsInsightsLoading(false);
    }
  }, [matchupConfig, isInsightsLoading, selectedLeague]);

  const fetchPlayerInsights = useCallback(async (config?: MatchupConfig, splitKeys?: string[], forceRefresh?: boolean) => {
    // Block all insights calls until configuration is fully loaded
    if (isLoadingConfigRef.current) {
      console.log('🔄 Configuration still loading, blocking player insights fetch');
      return;
    }
    
    const configToUse = config || matchupConfig;
    if (!configToUse) {
      console.log('No matchup config available for loading player insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return;
    }

    if (!selectedLeague) {
      console.log('❌ [INSIGHTS DEBUG] No league configured for loading player insights - will retry after configuration is loaded');
      setInsightsError('League is not configured. Please select a league in settings.');
      
      // Retry after a short delay to allow configuration to complete
      setTimeout(() => {
        if (!isLoadingConfigRef.current && selectedLeague) {
          console.log('🔄 [INSIGHTS DEBUG] Retrying player insights fetch - configuration and league are now ready');
          fetchPlayerInsights(config, splitKeys, forceRefresh);
        } else {
          console.log('🔄 [INSIGHTS DEBUG] Retry conditions not met - still loading or no league');
        }
      }, 500);
      return;
    }

    // Note: Removed currentStatsType check to prevent race condition
    // The calling code already knows which type to fetch

    // REMOVE the check for already loaded insights
    // This allows refetching when splits change

    if (isInsightsLoading) {
      console.log('Insights already loading, skipping duplicate call');
      return;
    }

    setIsInsightsLoading(true);
    setInsightsError(null);

    try {
      console.log(`Loading player insights only for ${configToUse.season} ${configToUse.matchup_type} season${forceRefresh ? ' (force refresh)' : ''}`);
      
      // Only fetch opponent insights - pre-game page only shows opponent data
      const opponentInsights = await apiService.getPlayersInsights(
        configToUse.opponent_team.teamId,
        configToUse.season,
        configToUse.matchup_type,
        splitKeys,
        selectedLeague.abbreviation,
        forceRefresh
      );
      
      setOpponentPlayerInsights(opponentInsights);
      console.log('✅ Player insights loaded');

    } catch (error) {
      console.error('❌ Error loading player insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to load player insights');
    } finally {
      setIsInsightsLoading(false);
    }
  }, [matchupConfig, isInsightsLoading, selectedLeague]);

  const fetchLineupInsights = useCallback(async (config?: MatchupConfig, splitKeys?: string[], forceRefresh?: boolean) => {
    // Block all insights calls until configuration is fully loaded
    if (isLoadingConfigRef.current) {
      console.log('🔄 Configuration still loading, blocking lineup insights fetch');
      return;
    }
    
    const configToUse = config || matchupConfig;
    if (!configToUse) {
      console.log('No matchup config available for loading lineup insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return;
    }

    if (!selectedLeague) {
      console.log('❌ [INSIGHTS DEBUG] No league configured for loading lineup insights - will retry after configuration is loaded');
      setInsightsError('League is not configured. Please select a league in settings.');
      
      // Retry after a short delay to allow configuration to complete
      setTimeout(() => {
        if (!isLoadingConfigRef.current && selectedLeague) {
          console.log('🔄 [INSIGHTS DEBUG] Retrying lineup insights fetch - configuration and league are now ready');
          fetchLineupInsights(config, splitKeys, forceRefresh);
        } else {
          console.log('🔄 [INSIGHTS DEBUG] Retry conditions not met - still loading or no league');
        }
      }, 500);
      return;
    }

    // Note: Removed currentStatsType check to prevent race condition
    // The calling code already knows which type to fetch

    // REMOVE the check for already loaded insights
    // This allows refetching when splits change

    if (isInsightsLoading) {
      console.log('Insights already loading, skipping duplicate call');
      return;
    }

    setIsInsightsLoading(true);
    setInsightsError(null);

    try {
      console.log(`Loading lineup insights only for ${configToUse.season} ${configToUse.matchup_type} season${forceRefresh ? ' (force refresh)' : ''}`);
      
      // Only fetch opponent insights - pre-game page only shows opponent data
      const opponentInsights = await apiService.getLineupsInsights(
        configToUse.opponent_team.teamId,
        configToUse.season,
        configToUse.matchup_type,
        5,
        splitKeys,
        selectedLeague.abbreviation,
        forceRefresh
      );
      
      setOpponentLineupsInsights(opponentInsights);
      console.log('✅ Lineup insights loaded');

    } catch (error) {
      console.error('❌ Error loading lineup insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to load lineup insights');
    } finally {
      setIsInsightsLoading(false);
    }
  }, [matchupConfig, isInsightsLoading, selectedLeague]);

  // Insights refresh methods
  const refreshTeamInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing team insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      setIsInsightsLoading(true);
      setInsightsError(null);
      console.log(`Refreshing team insights for ${matchupConfig.season} ${matchupConfig.matchup_type} season`);
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague!.abbreviation
      };

      const splitsToSend = ['overall'];
      if (splitKeys && splitKeys.length > 0) {
        splitsToSend.push(...splitKeys);
      }

      const refreshedInsights = await apiService.refreshTeamInsights(
        selectedLeague.abbreviation,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setTeamInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Team insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Team insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing team insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh team insights');
      return null;
    } finally {
      setIsInsightsLoading(false);
    }
  }, [matchupConfig, selectedLeague]);

  const refreshPlayerInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing player insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      setIsInsightsLoading(true);
      setInsightsError(null);
      console.log(`Refreshing player insights for ${matchupConfig.season} ${matchupConfig.matchup_type} season`);
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague!.abbreviation
      };

      const splitsToSend = ['overall'];
      if (splitKeys && splitKeys.length > 0) {
        splitsToSend.push(...splitKeys);
      }

      const refreshedInsights = await apiService.refreshPlayerInsights(
        selectedLeague.abbreviation,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setPlayerInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Player insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Player insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing player insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh player insights');
      return null;
    } finally {
      setIsInsightsLoading(false);
    }
  }, [matchupConfig, selectedLeague]);

  const refreshLineupInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing lineup insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      setIsInsightsLoading(true);
      setInsightsError(null);
      console.log(`Refreshing lineup insights for ${matchupConfig.season} ${matchupConfig.matchup_type} season`);
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague!.abbreviation
      };

      const splitsToSend = ['overall'];
      if (splitKeys && splitKeys.length > 0) {
        splitsToSend.push(...splitKeys);
      }

      const refreshedInsights = await apiService.refreshLineupInsights(
        selectedLeague.abbreviation,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setLineupsInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Lineup insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Lineup insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing lineup insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh lineup insights');
      return null;
    } finally {
      setIsInsightsLoading(false);
    }
  }, [matchupConfig, selectedLeague]);

  // Opponent insights refresh methods
  const refreshOpponentTeamInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing opponent team insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      setIsInsightsLoading(true);
      setInsightsError(null);
      console.log('Refreshing opponent team insights for', matchupConfig.season, matchupConfig.matchup_type, 'season');
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague!.id
      };

      const splitsToSend = splitKeys && splitKeys.length > 0 ? splitKeys : ['win', 'loss'];
      
      const refreshedInsights = await apiService.refreshTeamInsights(
        selectedLeague!.id,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setOpponentTeamInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Opponent team insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Opponent team insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing opponent team insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh opponent team insights');
      return null;
    } finally {
      setIsInsightsLoading(false);
    }
  }, [matchupConfig, selectedLeague]);

  const refreshOpponentPlayerInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing opponent player insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      setIsInsightsLoading(true);
      setInsightsError(null);
      console.log('Refreshing opponent player insights for', matchupConfig.season, matchupConfig.matchup_type, 'season');
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague.id
      };

      const splitsToSend = splitKeys && splitKeys.length > 0 ? splitKeys : ['win', 'loss'];
      
      const refreshedInsights = await apiService.refreshPlayerInsights(
        selectedLeague!.id,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setOpponentPlayerInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Opponent player insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Opponent player insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing opponent player insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh opponent player insights');
      return null;
    } finally {
      setIsInsightsLoading(false);
    }
  }, [matchupConfig, selectedLeague]);

  const refreshOpponentLineupInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing opponent lineup insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      setIsInsightsLoading(true);
      setInsightsError(null);
      console.log('Refreshing opponent lineup insights for', matchupConfig.season, matchupConfig.matchup_type, 'season');
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague.id
      };

      const splitsToSend = splitKeys && splitKeys.length > 0 ? splitKeys : ['win', 'loss'];
      
      const refreshedInsights = await apiService.refreshLineupInsights(
        selectedLeague.id,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setOpponentLineupsInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Opponent lineup insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Opponent lineup insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing opponent lineup insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh opponent lineup insights');
      return null;
    } finally {
      setIsInsightsLoading(false);
    }
  }, [matchupConfig, selectedLeague]);

  // Individual insight refresh methods (no global loading state)
  const refreshIndividualTeamInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing individual team insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      console.log(`Refreshing individual team insights for ${matchupConfig.season} ${matchupConfig.matchup_type} season`);
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague.abbreviation
      };

      const splitsToSend = ['overall'];
      if (splitKeys && splitKeys.length > 0) {
        splitsToSend.push(...splitKeys);
      }

      const refreshedInsights = await apiService.refreshTeamInsights(
        selectedLeague.abbreviation,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setTeamInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Individual team insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Individual team insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing individual team insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh individual team insights');
      return null;
    }
  }, [matchupConfig, selectedLeague]);

  const refreshIndividualPlayerInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing individual player insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      console.log(`Refreshing individual player insights for ${matchupConfig.season} ${matchupConfig.matchup_type} season`);
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague.abbreviation
      };

      const splitsToSend = ['overall'];
      if (splitKeys && splitKeys.length > 0) {
        splitsToSend.push(...splitKeys);
      }

      const refreshedInsights = await apiService.refreshPlayerInsights(
        selectedLeague.abbreviation,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setPlayerInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Individual player insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Individual player insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing individual player insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh individual player insights');
      return null;
    }
  }, [matchupConfig, selectedLeague]);

  const refreshIndividualLineupInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing individual lineup insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      console.log(`Refreshing individual lineup insights for ${matchupConfig.season} ${matchupConfig.matchup_type} season`);
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague.abbreviation
      };

      const splitsToSend = ['overall'];
      if (splitKeys && splitKeys.length > 0) {
        splitsToSend.push(...splitKeys);
      }

      const refreshedInsights = await apiService.refreshLineupInsights(
        selectedLeague.abbreviation,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setLineupsInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Individual lineup insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Individual lineup insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing individual lineup insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh individual lineup insights');
      return null;
    }
  }, [matchupConfig, selectedLeague]);

  const refreshIndividualOpponentTeamInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing individual opponent team insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      console.log('Refreshing individual opponent team insights for', matchupConfig.season, matchupConfig.matchup_type, 'season');
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague.id
      };

      const splitsToSend = splitKeys && splitKeys.length > 0 ? splitKeys : ['win', 'loss'];
      
      const refreshedInsights = await apiService.refreshTeamInsights(
        selectedLeague.id,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setOpponentTeamInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Individual opponent team insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Individual opponent team insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing individual opponent team insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh individual opponent team insights');
      return null;
    }
  }, [matchupConfig, selectedLeague]);

  const refreshIndividualOpponentPlayerInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing individual opponent player insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      console.log('Refreshing individual opponent player insights for', matchupConfig.season, matchupConfig.matchup_type, 'season');
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague.id
      };

      const splitsToSend = splitKeys && splitKeys.length > 0 ? splitKeys : ['win', 'loss'];
      
      const refreshedInsights = await apiService.refreshPlayerInsights(
        selectedLeague.id,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setOpponentPlayerInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Individual opponent player insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      console.log('✅ Individual opponent player insights refreshed');
      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing individual opponent player insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh individual opponent player insights');
      return null;
    }
  }, [matchupConfig, selectedLeague]);

  const refreshIndividualOpponentLineupInsights = useCallback(async (insightIds: string[], splitKeys?: string[], insightIndex?: number): Promise<InsightsResponse | null> => {
    if (!matchupConfig || !selectedLeague) {
      console.log('No matchup config or league available for refreshing individual opponent lineup insights');
      setInsightsError('No team configuration available. Please configure teams first.');
      return null;
    }

    try {
      console.log('Refreshing individual opponent lineup insights for', matchupConfig.season, matchupConfig.matchup_type, 'season');
      
      const matchConfig = {
        stage: matchupConfig.matchup_type,
        userTeam: {
          teamId: matchupConfig.user_team.teamId,
          teamName: matchupConfig.user_team.teamName
        },
        opponentTeam: {
          teamId: matchupConfig.opponent_team.teamId,
          teamName: matchupConfig.opponent_team.teamName
        },
        seasonId: matchupConfig.season,
        league: selectedLeague.id
      };

      const splitsToSend = splitKeys && splitKeys.length > 0 ? splitKeys : ['win', 'loss'];
      
      const refreshedInsights = await apiService.refreshLineupInsights(
        selectedLeague.id,
        insightIds,
        splitsToSend,
        matchConfig
      );

      // Replace the refreshed insights (replace old one at specific position with new one)
      if (refreshedInsights && refreshedInsights.insights) {
        setOpponentLineupsInsights((prev: any) => {
          const existingInsights = prev?.insights || [];
          
          let newInsights;
          if (insightIndex !== undefined && insightIndex >= 0) {
            // Replace the insight at the specific position with the new one
            newInsights = [...existingInsights];
            newInsights.splice(insightIndex, 1, ...refreshedInsights.insights);
          } else {
            // For bulk refresh, remove all and add new ones
            newInsights = [...refreshedInsights.insights];
          }
          
          console.log('🔄 Individual opponent lineup insights updated:', {
            originalCount: existingInsights.length,
            refreshedCount: refreshedInsights.insights.length,
            newTotalCount: newInsights.length,
            insightIndex,
            refreshedIds: refreshedInsights.insights.map(insight => insight.id)
          });
          
          return {
            ...prev,
            insights: newInsights,
            lastUpdated: Date.now() // Force re-render
          };
        });
      }

      return refreshedInsights;

    } catch (error) {
      console.error('❌ Error refreshing individual opponent lineup insights:', error);
      setInsightsError(error instanceof Error ? error.message : 'Failed to refresh individual opponent lineup insights');
      return null;
    }
  }, [matchupConfig, selectedLeague]);

  // Load all teams
  const loadAllTeams = useCallback(async () => {
    try {
      // Teams will be loaded from API when a league is selected
      // This function is kept for backward compatibility but teams are now loaded via loadTeamsByLeague
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  }, []);

  // Load leagues
  const loadLeagues = useCallback(async () => {
    // Prevent duplicate calls
    if (isLoadingLeaguesRef.current || leagues.length > 0) {
      return;
    }
    
    try {
      isLoadingLeaguesRef.current = true;
      const leaguesData = await apiService.getLeagues();
      
      // Create a Set to remove duplicates and normalize league names
      const uniqueLeagues = new Set<string>();
      const leagueMap = new Map<string, { name: string; abbreviation: string }>();
      
      leaguesData.forEach((leagueString: string) => {
        const normalizedId = leagueString.toLowerCase();
        
        // Skip if we already have this league
        if (uniqueLeagues.has(normalizedId)) return;
        
        uniqueLeagues.add(normalizedId);
        
        // Use league string as-is for both name and abbreviation
        leagueMap.set(normalizedId, {
          name: leagueString,
          abbreviation: leagueString
        });
      });
      
      // Transform to League objects
      const transformedLeagues = Array.from(uniqueLeagues).map(leagueId => ({
        id: leagueId,
        name: leagueMap.get(leagueId)?.name || leagueId,
        abbreviation: leagueMap.get(leagueId)?.abbreviation || leagueId,
        country: 'USA'
      }));
      
      setLeagues(transformedLeagues);
    } catch (error) {
      console.error('Failed to load leagues:', error);
      // Only show leagues that API returns, do not add default fallback
      setLeagues([]);
    } finally {
      isLoadingLeaguesRef.current = false;
    }
  }, [leagues.length]);

  // Load stages for a league
  const loadStages = useCallback(async (league: string) => {
    const leagueKey = league.toUpperCase();
    
    // Prevent duplicate calls for the same league
    if (isLoadingStagesRef.current === leagueKey) {
      return;
    }
    
    // If stages are already loaded for this league, skip
    if (stages.length > 0 && selectedLeague?.id === league.toLowerCase()) {
      return;
    }
    
    try {
      isLoadingStagesRef.current = leagueKey;
      setIsStagesLoading(true);
      setStagesError(null);
      
      const stagesData = await apiService.getStages(leagueKey);
      setStages(stagesData);
    } catch (error) {
      console.error(`Failed to load stages for league ${league}:`, error);
      setStagesError(error instanceof Error ? error.message : 'Failed to load stages');
      // Set empty array on error
      setStages([]);
    } finally {
      setIsStagesLoading(false);
      isLoadingStagesRef.current = null;
    }
  }, [selectedLeague, stages.length]);

  // Load teams by league
  const loadTeamsByLeague = useCallback(async (league: string) => {
    const leagueKey = league.toUpperCase();
    
    // Prevent duplicate calls for the same league
    if (isLoadingTeamsRef.current === leagueKey) {
      return;
    }
    
    // If teams are already loaded for this league, skip
    if (allTeams.length > 0 && selectedLeague?.id === league.toLowerCase()) {
      return;
    }
    
    try {
      isLoadingTeamsRef.current = leagueKey;
      // Convert league ID to uppercase for API call
      const teamsData = await apiService.getTeamsByLeague(leagueKey);
      
      // API response already uses {teamId, teamName} format
      const transformedTeams = teamsData.map(team => ({
        teamId: team.teamId,      // Use teamId directly from API
        teamName: team.teamName,  // Use teamName directly from API
        conference: team.conference || 'Unknown' // Keep other fields
      }));
      
      setAllTeams(transformedTeams);
      setEasternTeams(transformedTeams.filter((team: any) => team.conference === "Eastern"));
      setWesternTeams(transformedTeams.filter((team: any) => team.conference === "Western"));
    } catch (error) {
      console.error(`Failed to load teams for league ${league}:`, error);
    } finally {
      isLoadingTeamsRef.current = null;
    }
  }, [allTeams.length, selectedLeague]);

  // Set team configuration
  const setTeamConfiguration = useCallback(async (config: TeamConfigRequest): Promise<boolean> => {
    try {
      setIsConfiguring(true);
      setError(null);
      
      
      // Find team info - try to find in allTeams first
      let userTeam = allTeams.find(team => team.teamId === config.user_team_abbreviation);
      let opponentTeam = allTeams.find(team => team.teamId === config.opponent_team_abbreviation);
      
      // If user team not found, it might be an organization team
      // Try to fetch it from the organization endpoint
      if (!userTeam) {
        try {
          const orgTeamResponse = await apiFetch('/api/v1/organizations/me/team');
          if (orgTeamResponse.ok) {
            const orgTeam = await orgTeamResponse.json();
            if (orgTeam.teamId === config.user_team_abbreviation) {
              userTeam = orgTeam;
              console.log('🔧 Found user team from organization:', userTeam);
            }
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch organization team:', err);
        }
      }
      
      
      if (!userTeam || !opponentTeam) {
        const missingTeams = [];
        if (!userTeam) missingTeams.push(`User team: ${config.user_team_abbreviation}`);
        if (!opponentTeam) missingTeams.push(`Opponent team: ${config.opponent_team_abbreviation}`);
        const errorMsg = `Teams not found: ${missingTeams.join(', ')}. Please ensure teams are loaded for the selected league.`;
        console.error('❌', errorMsg);
        console.error('Available teams:', allTeams.map(t => t.teamId));
        throw new Error(errorMsg);
      }
      
      const matchupConfig: MatchupConfig = {
        user_team: userTeam,
        opponent_team: opponentTeam,
        season: config.season,
        matchup_type: config.matchup_type,
        league: config.league
      };
      
      // Clear any cached recommendations/insights so the new config fetches fresh data
      clearRecommendationCaches();
      lastPrefetchKeyRef.current = null;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('clearReportsCache'));
      }

      // Set the configuration locally first
      setMatchupConfig(matchupConfig);
      
      // Save to backend (user's last configuration)
      const leagueId = selectedLeague?.id || selectedLeague?.abbreviation || '';
      if (!leagueId) {
        const errorMsg = 'No league selected. Cannot save configuration to backend.';
        console.error('❌', errorMsg);
        throw new Error(errorMsg);
      }
      
      await apiService.setTeamConfig(matchupConfig, leagueId);
      console.log('🔧 Configuration saved to backend');
      
      return true;
    } catch (error) {
      console.error('❌ Error in setTeamConfiguration:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to set team configuration';
      setError(errorMessage);
      return false;
    } finally {
      setIsConfiguring(false);
    }
  }, [allTeams, setMatchupConfig, selectedLeague, clearRecommendationCaches]);

  const prefetchRecommendations = useCallback(async (config: MatchupConfig | null) => {
    if (!config || !selectedLeague?.abbreviation) {
      lastPrefetchKeyRef.current = null;
      return;
    }

    if (!config.user_team?.teamId || !config.opponent_team?.teamId) {
      return;
    }

    if (!config.season) {
      console.warn('⚠️ Cannot prefetch recommendations without a season');
      return;
    }

    const opponentTeamId = config.opponent_team.teamId;
    const ourTeamId = config.user_team.teamId;
    const stageForCache = config.matchup_type || 'regular_season';
    const stageForRequest = config.matchup_type || 'regular';
    const seasonId = config.season;
    const leagueKey = selectedLeague.abbreviation;

    const prefetchKey = `${ourTeamId}_${opponentTeamId}_${seasonId}_${stageForCache}_${leagueKey}`;
    if (lastPrefetchKeyRef.current === prefetchKey) {
      return;
    }
    lastPrefetchKeyRef.current = prefetchKey;

    const cachedOffensive = getRecommendationCache('offensive', opponentTeamId, ourTeamId, seasonId, stageForCache);
    const cachedDefensive = getRecommendationCache('defensive', opponentTeamId, ourTeamId, seasonId, stageForCache);

    if (cachedOffensive && cachedDefensive) {
      return;
    }

    const matchConfigPayload = {
      stage: stageForRequest,
      userTeam: {
        teamId: ourTeamId,
        teamName: config.user_team.teamName || ''
      },
      opponentTeam: {
        teamId: opponentTeamId,
        teamName: config.opponent_team.teamName || ''
      },
      seasonId,
      league: selectedLeague.abbreviation
    };

    const requests: Promise<void>[] = [];

    if (!cachedOffensive) {
      requests.push(
        apiService
          .getOffensiveRecommendations(matchConfigPayload)
          .then(data => {
            clearRecommendationRequestInFlight('offensive', buildRecommendationCacheKey('offensive', opponentTeamId, ourTeamId, seasonId, stageForCache));
            setRecommendationCache('offensive', opponentTeamId, ourTeamId, data, seasonId, stageForCache);
          })
          .catch(err => {
            clearRecommendationRequestInFlight('offensive', buildRecommendationCacheKey('offensive', opponentTeamId, ourTeamId, seasonId, stageForCache));
            throw err;
          })
      );
      markRecommendationRequestInFlight('offensive', buildRecommendationCacheKey('offensive', opponentTeamId, ourTeamId, seasonId, stageForCache));
    }

    if (!cachedDefensive) {
      requests.push(
        apiService
          .getDefensiveRecommendations(matchConfigPayload)
          .then(data => {
            clearRecommendationRequestInFlight('defensive', buildRecommendationCacheKey('defensive', opponentTeamId, ourTeamId, seasonId, stageForCache));
            setRecommendationCache('defensive', opponentTeamId, ourTeamId, data, seasonId, stageForCache);
          })
          .catch(err => {
            clearRecommendationRequestInFlight('defensive', buildRecommendationCacheKey('defensive', opponentTeamId, ourTeamId, seasonId, stageForCache));
            throw err;
          })
      );
      markRecommendationRequestInFlight('defensive', buildRecommendationCacheKey('defensive', opponentTeamId, ourTeamId, seasonId, stageForCache));
    }

    if (!requests.length) {
      return;
    }

    try {
      await Promise.all(requests);
    } catch (error) {
      console.error('❌ Failed to prefetch recommendations:', error);
      // Allow retry on failure
      lastPrefetchKeyRef.current = null;
    }
  }, [selectedLeague]);

  // Refresh configuration - loads from backend only
  const refreshConfiguration = useCallback(async () => {
    console.log('🔍 [CONFIG DEBUG] refreshConfiguration called');
    
    // Prevent duplicate calls
    if (isLoadingConfigRef.current) {
      console.log('⚠️ [CONFIG DEBUG] Configuration already loading, skipping duplicate call');
      return;
    }
    
    try {
      isLoadingConfigRef.current = true;
      console.log('🔄 [CONFIG DEBUG] isLoadingConfigRef.current set to true');
      
      // Load from backend (user's last configuration)
      console.log('🔍 [CONFIG DEBUG] Calling apiService.getTeamConfig()...');
      const backendConfig = await apiService.getTeamConfig();
      console.log('🔍 [CONFIG DEBUG] Backend response:', backendConfig);
      console.log('🔍 [CONFIG DEBUG] backendConfig type:', typeof backendConfig);
      console.log('🔍 [CONFIG DEBUG] backendConfig keys:', backendConfig ? Object.keys(backendConfig) : 'null/undefined');
      
      if (backendConfig?.config) {
        console.log('✅ [CONFIG DEBUG] Found configuration in backend:', backendConfig.config);
        const savedConfig = backendConfig.config;
        console.log('🔍 [CONFIG DEBUG] Saved config structure:', {
          league: savedConfig.league,
          user_team_abbreviation: savedConfig.user_team_abbreviation,
          opponent_team_abbreviation: savedConfig.opponent_team_abbreviation,
          season: savedConfig.season,
          matchup_type: savedConfig.matchup_type,
          allKeys: Object.keys(savedConfig)
        });
        
        // Load leagues and teams if needed - use refs to get current values
        if (leaguesRef.current.length === 0) {
          await loadLeagues();
          // Wait a bit for leagues to load
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        // Find the league using current ref value
        console.log('🔍 [LEAGUE DEBUG] Looking for league:', savedConfig.league);
        console.log('🔍 [LEAGUE DEBUG] Available leagues:', leaguesRef.current.map(l => ({ id: l.id, abbreviation: l.abbreviation, name: l.name })));
        const league = leaguesRef.current.find(l => l.name === savedConfig.league);
        if (league) {
          console.log('✅ [LEAGUE DEBUG] Found matching league:', league);
          setSelectedLeague(league);
        } else {
          console.log('❌ [LEAGUE DEBUG] No matching league found for:', savedConfig.league);
          console.log('🔍 [LEAGUE DEBUG] Available league IDs:', leaguesRef.current.map(l => l.id));
          console.log('🔍 [LEAGUE DEBUG] Available league abbreviations:', leaguesRef.current.map(l => l.abbreviation));
        }
        
        // Load teams for the league if needed - check current ref value
        if (savedConfig.league && allTeamsRef.current.length === 0) {
          await loadTeamsByLeague(savedConfig.league);
          // Wait a bit for teams to load
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        // Find teams using current ref value
        const userTeam = allTeamsRef.current.find(team => team.teamId === savedConfig.user_team_abbreviation);
        const opponentTeam = allTeamsRef.current.find(team => team.teamId === savedConfig.opponent_team_abbreviation);
        
        if (userTeam && opponentTeam) {
          console.log('✅ [CONFIG DEBUG] Successfully found teams, creating matchup config');
          const matchupConfig: MatchupConfig = {
            user_team: userTeam,
            opponent_team: opponentTeam,
            season: savedConfig.season,
            matchup_type: savedConfig.matchup_type,
            league: savedConfig.league
          };
          console.log('✅ [CONFIG DEBUG] Setting matchup config from backend:', matchupConfig);
          setMatchupConfig(matchupConfig);
        } else {
          console.warn('⚠️ [CONFIG DEBUG] Teams not found for saved configuration');
          if (!userTeam) console.warn('⚠️ [CONFIG DEBUG] User team not found:', savedConfig.user_team_abbreviation);
          if (!opponentTeam) console.warn('⚠️ [CONFIG DEBUG] Opponent team not found:', savedConfig.opponent_team_abbreviation);
        }
      } else {
        console.log('⚠️ [CONFIG DEBUG] No configuration found in backend - user must configure manually');
        // No default league selection - user must configure manually
      }
    } catch (error) {
      console.error('❌ [CONFIG DEBUG] Failed to load configuration from backend:', error);
      // No localStorage fallback - configuration must come from backend
    } finally {
      console.log('🏁 [CONFIG DEBUG] refreshConfiguration completed, setting isLoadingConfigRef.current to false');
      isLoadingConfigRef.current = false;
    }
  }, [setMatchupConfig, loadLeagues, loadTeamsByLeague]);

  useEffect(() => {
    prefetchRecommendations(matchupConfig);
  }, [matchupConfig, prefetchRecommendations]);

  // Clear configuration
  const clearConfiguration = useCallback(async () => {
    try {
      setMatchupConfigState(null);
      setMatchupSummary(null);
      setSelectedLeague(null);
      setOpponentTeamStats(null);
      setUserTeamStats(null);
    } catch (error) {
      console.error('Failed to clear configuration:', error);
    }
  }, []);

  // Clear cache - this will be called by Reports component
  const clearCache = useCallback(() => {
    // Clear opponent insights as well
    setOpponentTeamInsights(null);
    setOpponentPlayerInsights(null);
    setOpponentLineupsInsights(null);
    // This function will be called by the Reports component to clear its caches
    // We'll trigger a custom event that the Reports component can listen to
    window.dispatchEvent(new CustomEvent('clearReportsCache'));
  }, []);

  // Force reload configuration from backend
  const forceReloadConfiguration = useCallback(async () => {
    try {
      console.log('🔄 Force reloading configuration from backend...');
      await refreshConfiguration();
    } catch (error) {
      console.error('❌ Failed to force reload configuration:', error);
    }
  }, [refreshConfiguration]);

  // Load team splits
  const loadTeamSplits = useCallback(async (league: string) => {
    // Prevent duplicate calls if already loading or already loaded
    if (isSplitsLoading) {
      return;
    }
    
    if (teamSplits && teamSplits.length > 0) {
      return;
    }

    setIsSplitsLoading(true);
    setSplitsError(null);

    try {
      const splits = await apiService.getTeamSplits(league);
      setTeamSplits(splits);
      
      // Check if "wins" and "losses" splits exist and set as default
      if (splits && splits.includes('wins') && splits.includes('losses')) {
        setDefaultSplits(['wins', 'losses']);
      } else {
        console.log('⚠️ Wins and losses splits not both available, no default splits set');
      }
    } catch (error) {
      console.error('❌ Error loading team splits:', error);
      setSplitsError(error instanceof Error ? error.message : 'Failed to load team splits');
    } finally {
      setIsSplitsLoading(false);
    }
  }, [isSplitsLoading, teamSplits]);

  // Load players splits
  const loadPlayersSplits = useCallback(async (league: string) => {
    // Prevent duplicate calls if already loading or already loaded
    if (isSplitsLoading) {
      console.log('🔄 Players splits already loading, skipping duplicate call');
      return;
    }
    
    if (playerSplits && playerSplits.length > 0) {
      console.log('🔄 Players splits already loaded, skipping duplicate call');
      return;
    }

    setIsSplitsLoading(true);
    setSplitsError(null);

    try {
      const splits = await apiService.getPlayersSplits(league);
      setPlayerSplits(splits);
      console.log('✅ Players splits loaded:', splits);
    } catch (error) {
      console.error('❌ Error loading players splits:', error);
      setSplitsError(error instanceof Error ? error.message : 'Failed to load players splits');
    } finally {
      setIsSplitsLoading(false);
    }
  }, [isSplitsLoading, playerSplits]);

  // Load lineups splits
  const loadLineupsSplits = useCallback(async (league: string) => {
    // Prevent duplicate calls if already loading or already loaded
    if (isSplitsLoading) {
      console.log('🔄 Lineups splits already loading, skipping duplicate call');
      return;
    }
    
    if (lineupSplits && lineupSplits.length > 0) {
      console.log('🔄 Lineups splits already loaded, skipping duplicate call');
      return;
    }

    setIsSplitsLoading(true);
    setSplitsError(null);

    try {
      const splits = await apiService.getLineupsSplits(league);
      setLineupSplits(splits);
      console.log('✅ Lineups splits loaded:', splits);
    } catch (error) {
      console.error('❌ Error loading lineups splits:', error);
      setSplitsError(error instanceof Error ? error.message : 'Failed to load lineups splits');
    } finally {
      setIsSplitsLoading(false);
    }
  }, [isSplitsLoading, lineupSplits]);

  // Helper methods
  const getUserTeam = useCallback((): TeamInfo | null => {
    return matchupConfig?.user_team || null;
  }, [matchupConfig]);

  const getOpponentTeam = useCallback((): TeamInfo | null => {
    return matchupConfig?.opponent_team || null;
  }, [matchupConfig]);

  const getMatchupTitle = useCallback((): string => {
    if (matchupSummary) {
      return matchupSummary.matchup_title;
    }
    
    if (matchupConfig) {
      return `${matchupConfig.user_team.teamName} vs ${matchupConfig.opponent_team.teamName}`;
    }
    
    return 'Loading...';
  }, [matchupSummary, matchupConfig]);

  // Load initial team configuration and teams
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        
        // Load leagues first (refreshConfiguration will skip if already loaded)
        await loadLeagues();
        
        // Small delay to ensure leagues state is updated
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Then load configuration (will load teams for the saved league if needed)
        await refreshConfiguration();
        
        // Note: Insights are now loaded only when user selects splits
        // No initial insights loading to avoid unnecessary API calls
        
      } catch (error) {
        console.error('❌ [INIT DEBUG] Failed to load initial data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Reload configuration when user logs in
  useEffect(() => {
    if (user && !matchupConfig) {
      refreshConfiguration();
    }
  }, [user, matchupConfig, refreshConfiguration]);

  // Reload configuration when critical parameters are missing
  useEffect(() => {
    const checkAndReloadConfig = () => {
      if (!selectedLeague) {
        refreshConfiguration();
        return;
      }
      
      if (!matchupConfig) {
        refreshConfiguration();
        return;
      }
      
      if (leagues.length === 0) {
        refreshConfiguration();
        return;
      }
      
      if (allTeams.length === 0) {
        refreshConfiguration();
        return;
      }
    };
    
    checkAndReloadConfig();
  }, [selectedLeague, matchupConfig, leagues.length, allTeams.length, refreshConfiguration]);

  // Load teams and stages when league changes
  useEffect(() => {
    if (selectedLeague) {
      loadTeamsByLeague(selectedLeague.id);
      loadStages(selectedLeague.id);
    }
  }, [selectedLeague, loadTeamsByLeague, loadStages]);

  // Clear cache when matchup config changes
  useEffect(() => {
    if (matchupConfig) {
      clearCache();
    }
  }, [matchupConfig, clearCache]);

  // Note: Removed automatic insights fetching - insights will only be loaded when explicitly requested
  // This prevents duplicate API calls and keeps the behavior simple

  // Note: Team stats loading is handled by individual components (like Reports) 
  // when they detect matchupConfig changes, to avoid duplicate API calls


  const value: TeamContextType = {
    // Team configuration
    matchupConfig,
    setMatchupConfig,
    matchupSummary,
    
    // League data
    leagues,
    selectedLeague,
    setSelectedLeague,
    
    // Stage data
    stages,
    isStagesLoading,
    stagesError,
    
    // Team data
    userTeam: matchupConfig?.user_team || null,
    opponentTeam: matchupConfig?.opponent_team || null,
    allTeams,
    easternTeams,
    westernTeams,
    
    // Team stats
    opponentTeamStats,
    userTeamStats,
    isStatsLoading,
    statsError,
    
    // Team insights
    teamInsights,
    playerInsights,
    lineupsInsights,
    opponentTeamInsights,
    opponentPlayerInsights,
    opponentLineupsInsights,
    isInsightsLoading,
    insightsError,
    
    // Splits data
    teamSplits,
    playerSplits,
    lineupSplits,
    isSplitsLoading,
    splitsError,
    defaultSplits,
    
    // Loading states
    isLoading,
    isConfiguring,
    error,
    
    // Actions
    loadTeamStats,
    refreshTeamStats,
    setTeamConfiguration,
    refreshConfiguration,
    clearConfiguration,
    loadAllTeams,
    loadLeagues,
    loadStages,
    loadTeamsByLeague,
    clearCache,
    forceReloadConfiguration,
    
    // Selective stats loading methods
    loadTeamStatsOnly,
    loadPlayerStatsOnly,
    loadLineupStatsOnly,
    clearCurrentStats,
    clearAllInsights,
    
    // Selective insights fetching methods
    fetchTeamInsights,
    fetchPlayerInsights,
    fetchLineupInsights,
    
    // Insights refresh methods
    refreshTeamInsights,
    refreshPlayerInsights,
    refreshLineupInsights,
    
    // Opponent insights refresh methods
    refreshOpponentTeamInsights,
    refreshOpponentPlayerInsights,
    refreshOpponentLineupInsights,
    
    // Individual insight refresh methods (no global loading state)
    refreshIndividualTeamInsights,
    refreshIndividualPlayerInsights,
    refreshIndividualLineupInsights,
    refreshIndividualOpponentTeamInsights,
    refreshIndividualOpponentPlayerInsights,
    refreshIndividualOpponentLineupInsights,
    
    // Splits methods
    loadTeamSplits,
    loadPlayersSplits,
    loadLineupsSplits,
    
    // Current stats type for selective loading
    currentStatsType,
    setCurrentStatsType,
    
    // Helper methods
    getUserTeam,
    getOpponentTeam,
    getMatchupTitle,
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};